// Secure-checkout redirect: Stripe.js refuses live-mode initialization outside
// a secure context, so a publisher page reached over plain http cannot run the
// embedded payment step. When that happens the checkout flows park the
// visitor's selection in an `ezsm_resume` query param and reload the page over
// https; bootstrap consumes the param and reopens checkout where the visitor
// left off. All URL manipulation goes through the URL API on a parsed copy of
// location.href — only `ezsm_resume` is ever added or removed, so publisher
// params (UTM, click ids, routing) and the hash survive byte-for-byte.

import type { ParkedSelection } from '@/types';
import { getSiteConfig } from '@/api/client';

const RESUME_PARAM = 'ezsm_resume';
const PROBE_TIMEOUT_MS = 4000;

export { RESUME_PARAM };

// SecureResumePayload is the selection parked in the URL across the
// http -> https reload. kind=product is a paywall/donation selection (product
// handle + chosen price); kind=checkout re-runs openCheckout({ price }) —
// including a product-scoped subscription or trial, since openCheckout sells
// those too. (kind=item is the legacy openCheckout tag, still accepted on
// read.) The shared selection fields come from ParkedSelection (types.ts);
// this envelope adds the wire version and the fields only this transport needs.
export interface SecureResumePayload extends ParkedSelection {
  v: 1;
  // Visitor-chosen amount for a variable donation; absent for fixed prices.
  amountCents?: number;
  // Set when the parked selection was a trial start, so resume routes through
  // the trial path rather than a paid checkout.
  trial?: boolean;
}

// isSecureContext wraps the platform getter so tests can stub the module
// instead of fighting jsdom's location (localhost is always trustworthy).
// A browser too old to expose the API reports "secure": without the API we
// cannot know, and defaulting to secure preserves the pre-interstitial
// behavior (Stripe.js itself errors) rather than newly gating those browsers.
export function isSecureContext(): boolean {
  return window.isSecureContext ?? true;
}

// checkoutNeedsSecureRedirect gates the secure-checkout interstitial. Live
// Stripe.js refuses to initialize outside a secure context, so an insecure
// (http) page must redirect. Test keys tolerate HTTP, so the interstitial is
// skipped when the serving backend reports it runs them (the site-config
// sandbox bit) — the single source of truth, which a sandbox-env local
// backend answers without any handoff, and the sandbox instance answers for
// a handoff preview (the exchange re-points the API base there first).
// A config that can't be loaded says nothing, and the interstitial stands.
export async function checkoutNeedsSecureRedirect(): Promise<boolean> {
  if (isSecureContext()) {
    return false;
  }
  const config = await getSiteConfig();
  return config?.sandbox !== true;
}

function encodePayload(payload: SecureResumePayload): string {
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodePayload(raw: string): SecureResumePayload | null {
  try {
    const base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Partial<SecureResumePayload>;
    if (!parsed || parsed.v !== 1 || (parsed.kind !== 'product' && parsed.kind !== 'item' && parsed.kind !== 'checkout')) {
      return null;
    }
    return parsed as SecureResumePayload;
  } catch {
    return null;
  }
}

// buildSecureUrl returns the current page over https with the resume payload
// attached. Only the protocol and the ezsm_resume param change; every existing
// param (order included) and the hash are preserved by the URL API.
export function buildSecureUrl(payload: SecureResumePayload): string {
  const url = new URL(window.location.href);
  url.protocol = 'https:';
  url.searchParams.set(RESUME_PARAM, encodePayload(payload));
  return url.toString();
}

// buildLocalAliasUrl parks the selection on the *.localhost alias of the
// current host, staying on http — Chrome/Firefox treat *.localhost as a
// secure context, so live checkout runs there with no TLS on the dev server.
// Only the hostname and the ezsm_resume param change. Callers must not be on
// a *.localhost alias already (the suffix would double).
export function buildLocalAliasUrl(payload: SecureResumePayload): string {
  const url = new URL(window.location.href);
  url.hostname = `${url.hostname}.localhost`;
  url.searchParams.set(RESUME_PARAM, encodePayload(payload));
  return url.toString();
}

// parseResumeParam reads and validates the resume payload from a query string,
// returning null when it is absent or malformed (a tampered param must never
// break page load).
export function parseResumeParam(search: string): SecureResumePayload | null {
  const raw = new URLSearchParams(search).get(RESUME_PARAM);
  if (!raw) {
    return null;
  }
  return decodePayload(raw);
}

// stripResumeParam removes only ezsm_resume from the address bar, leaving every
// other param and the hash exactly as they were.
export function stripResumeParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(RESUME_PARAM)) {
    return;
  }
  url.searchParams.delete(RESUME_PARAM);
  window.history.replaceState(null, '', url.toString());
}

// probeHttpsReachable reports whether the site's https endpoint answers. An
// http page fetching an https resource is not mixed content, and no-cors
// sidesteps CORS: any response (opaque included) means reachable; a reject or
// timeout means no https listener or an invalid/expired certificate.
export async function probeHttpsReachable(): Promise<boolean> {
  try {
    await fetch(`https://${window.location.host}${window.location.pathname}`, {
      mode: 'no-cors',
      cache: 'no-store',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return true;
  } catch {
    return false;
  }
}

// navigate wraps location.assign so tests can stub it (jsdom cannot navigate).
// assign (not replace) keeps the http page in history so Back works if the
// https load fails.
export function navigate(url: string): void {
  window.location.assign(url);
}
